import type { GA4Event } from './ga4Types';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Checks if the current environment is debug mode (local development).
 * Debug mode is active when NEXT_PUBLIC_ENV is not PROD, STAGING, or DEV.
 */
const isDebugMode = (): boolean => {
  const env = process.env.NEXT_PUBLIC_ENV;
  return !(env === 'PROD' || env === 'STAGING' || env === 'DEV');
};

/**
 * Dispatches a GA4 event to GTM via dataLayer.
 * Use this for GA4 ecommerce events that require specific parameter structure.
 *
 * Unlike useLogger methods, this function sends only the provided parameters
 * without adding extra metadata (action, pageName, app_name, etc.)
 *
 * In debug mode (local environment), logs to console instead of dataLayer.
 *
 * @param eventData - The GA4 event object with discriminated 'event' property
 *
 * @example
 * ```typescript
 * // Purchase event
 * dispatchGTMEvent({
 *   event: 'purchase',
 *   ecommerce: { transaction_id: '123', value: 99.99, currency: 'USD', ... },
 *   user: { type: 'guest' },
 *   platform: 'web'
 * });
 *
 * // Express checkout click event
 * dispatchGTMEvent({
 *   event: 'express_checkout_click',
 *   ecommerce: { currency: 'USD', value: 99.99, ... },
 *   user: { type: 'guest' },
 *   platform: 'web',
 *   ...
 * });
 * ```
 */
export const dispatchGTMEvent = (eventData: GA4Event): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (isDebugMode()) {
    console.info('[GTM Debug] Event:', eventData.event);
    console.table(eventData);
    return;
  }

  if (!window.dataLayer) {
    return;
  }

  window.dataLayer.push(eventData);
};
